import { motion } from "framer-motion";
import { SignalHead, ApertureScan, PipelineFusion, AscentSignal, ConvergeOrbits, GlyphSports, GlyphDefence, GlyphAviation } from "./Illustrations";

const DOMAINS = [
  {
    name: "Sports",
    body: "Athletes operate at the edge of physical and mental capacity, where understanding cognitive state can shape training, recovery, and competition outcomes.",
  },
  {
    name: "Defence",
    body: "Personnel operate in high-pressure, high-stakes environments, where cognitive state can directly affect judgement and mission outcomes.",
  },
  {
    name: "Aviation",
    body: "Pilots and crew are required to maintain consistent performance over long and demanding operations, where a lapse in state can carry significant consequences.",
  },
];

const CHAPTERS = [
  {
    number: "01",
    title: "The most critical performance variable remains largely invisible.",
    paragraphs: [
      "Human performance depends heavily on cognitive capacity: the ability to think clearly, stay effective, and perform reliably under demanding conditions. Despite decades of research, cognitive state remains one of the least measurable and least understood variables in real-world settings. Physical performance can be tracked with precision. Cognitive performance, by comparison, is still largely inferred rather than measured.",
      "This gap has consequences. Decisions in high-stakes environments are often made without visibility into the cognitive state of the people making or executing them.",
    ],
  },
  {
    number: "02",
    title: "Wherever human performance matters, cognition matters.",
    paragraphs: [
      "The challenge of understanding cognitive state is not confined to one field. It appears across every domain where performance depends on judgement, consistency, and decision making under pressure, each with its own conditions and stakes.",
    ],
    domains: true,
  },
  {
    number: "03",
    title: "What if human cognitive performance could become understandable?",
    paragraphs: [
      "Advances in artificial intelligence and neuroscience are converging in ways that were not possible even a few years ago. Signals that were once too subtle, too noisy, or too difficult to interpret can now be captured and analysed with greater precision.",
      "This opens a question worth asking seriously: what would it mean to make cognitive performance as visible and trackable as physical performance already is?",
    ],
  },
  {
    number: "04",
    title: "A new foundation for understanding cognitive performance.",
    paragraphs: [
      "Qelp AI is developing technology at the intersection of neurotechnology, artificial intelligence, and neurocognitive science. Our approach combines biosensing with a proprietary analytical framework designed to interpret cognitive and physiological signals in real time.",
    ],
  },
  {
    number: "05",
    title: "Beyond measurement. Toward cognitive intelligence.",
    paragraphs: [
      "Measuring cognitive state is a starting point, not an end point. The longer term ambition is to build the infrastructure that allows cognitive performance to be understood, tracked, and supported the way physical performance already is: continuously, contextually, and with enough precision to inform real decisions.",
    ],
  },
  {
    number: "06",
    title: "Building for a new era of human intelligence.",
    paragraphs: [
      "We see this as part of a broader shift already underway, one where human cognition, advanced technology, and artificial intelligence increasingly intersect. Our work is focused on contributing meaningfully to that shift, grounded in rigorous science rather than speculation, and built with the discipline that high-stakes domains require.",
    ],
  },
];

const Manifesto = () => (
  <section id="manifesto" className="py-28 md:py-36 px-6 md:px-16 lg:px-24 max-w-7xl mx-auto" data-testid="manifesto-section">
    <motion.p
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="font-mono-qelp text-xs tracking-[0.3em] uppercase text-[#10B8B1]"
      data-testid="manifesto-overline"
    >
      The Manifesto
    </motion.p>

    <div className="mt-14 space-y-24 md:space-y-32">
      {CHAPTERS.map((ch, i) => (
        <motion.article
          key={ch.number}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="grid md:grid-cols-12 gap-6 md:gap-10 items-start border-t border-white/10 pt-14 md:pt-16"
          data-testid={`manifesto-chapter-${ch.number}`}
        >
          <div className="md:col-span-2 order-1">
            <span className="font-heading text-6xl md:text-7xl leading-none text-transparent [-webkit-text-stroke:1px_rgba(16,184,177,0.5)]">
              {ch.number}
            </span>
          </div>
          <div className={["01", "03", "04", "05", "06"].includes(ch.number) ? "order-3 md:order-none md:col-span-7 lg:col-span-6" : "order-3 md:order-none md:col-span-10 lg:col-span-8"}>
            <h3 className={`font-heading text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-slate-100 max-w-2xl ${ch.number === "03" ? "italic" : ""}`}>
              {ch.title}
            </h3>
            <div className="mt-6 space-y-5 max-w-xl">
              {ch.paragraphs.map((p, j) => (
                <p key={j} className={`text-sm sm:text-base text-slate-400 leading-relaxed ${ch.number === "01" && j === 0 ? "qelp-dropcap" : ""}`}>
                  {p}
                </p>
              ))}
            </div>

            {ch.domains && (
              <div className="mt-10 grid sm:grid-cols-3 gap-4" data-testid="manifesto-domains">
                {DOMAINS.map((d, k) => (
                  <motion.div
                    key={d.name}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: k * 0.1 }}
                    className="qelp-card rounded-2xl p-6"
                    data-testid={`domain-${d.name.toLowerCase()}`}
                  >
                    <div className="mb-4">
                      {d.name === "Sports" && <GlyphSports />}
                      {d.name === "Defence" && <GlyphDefence />}
                      {d.name === "Aviation" && <GlyphAviation />}
                    </div>
                    <p className="font-mono-qelp text-[10px] tracking-[0.25em] uppercase text-[#10B8B1]">{d.name}</p>
                    <p className="mt-3 text-xs sm:text-sm text-slate-400 leading-relaxed">{d.body}</p>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
          {["01", "03", "04", "05", "06"].includes(ch.number) && (
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
              className="flex order-2 md:order-none md:col-span-3 lg:col-span-4 items-center justify-center py-4 md:py-0"
              data-testid={`manifesto-illo-${ch.number}`}
            >
              {ch.number === "01" && <SignalHead />}
              {ch.number === "03" && <ApertureScan />}
              {ch.number === "04" && <PipelineFusion />}
              {ch.number === "05" && <AscentSignal />}
              {ch.number === "06" && <ConvergeOrbits />}
            </motion.div>
          )}
        </motion.article>
      ))}
    </div>
  </section>
);

export default Manifesto;
